import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './LabelPrintingPage.module.css'
import pa from './ProductActivityPage.module.css'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { ApiError, apiJson, inCurrentWorkspace } from '../lib/api'
import { PendingButton } from '../components/pending'
import { useTasks } from '../components/TaskCentre'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { LabelSettingsModal } from './labels/LabelSettingsModal'
import { PrintHistory } from './labels/PrintHistory'
import { PeoplePicker } from './labels/PeoplePicker'
import { JobPicker } from './labels/JobPicker'
import { DraftPreview } from './labels/DraftPreview'
import { LargeBatchConfirm } from './labels/LargeBatchConfirm'
import { renderTask } from './labels/render'
import {
  COLOURS,
  CONTENT,
  ID_MODE_NOTE,
  formatLine,
  formatsFor,
  idModeLine,
  labelAccess,
  notAllowed,
  plural,
  qrTypeLine,
  startContent,
  startFormat,
  type ContentField,
  type IdMode,
  type LabelJob,
  type LabelPerson,
  type LabelShortcut,
  type Purpose,
  type SettingsView,
} from './labels/model'

type Tab = 'create' | 'history'

const UNASSIGNED_OFF = 'Unassigned item labels are not enabled for this subscription.'

function Padlock() {
  return (
    <svg className={styles.padlock} width="13" height="13" viewBox="0 0 24 24" aria-label="Locked" role="img">
      <rect x="4" y="10" width="16" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2.75" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" />
    </svg>
  )
}

function Tick({ on, locked }: { on: boolean; locked?: boolean }) {
  return (
    <span className={[styles.tick, on ? styles.tickOn : '', locked ? styles.tickLocked : ''].join(' ')} aria-hidden="true">
      {on && (
        <svg width="12" height="12" viewBox="0 0 24 24">
          <path d="m5 13 4.5 4.5L19 7" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  )
}

function Gear() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  )
}

// One numbered stage (V4): a small uppercase label over its own card(s).
function Stage({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.stage}>
      <h3 className={styles.stageTitle}>{title}</h3>
      {children}
    </div>
  )
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'create', label: 'Create labels' },
  { key: 'history', label: 'Print history' },
]

// Label printing (§7m). Two panes, not a wizard (§7m.3): the numbered stages on
// the left, the generation summary and the draft preview pinned on the right.
// The page opens on Create labels with Item tracking as the purpose, and a new
// batch starts from the tenant's settings (§9.9): the default format for the
// purpose, the content boxes and the Display ID namespace. A capability the
// tenant does not hold is absent, not disabled (§7m.2).
export function LabelPrintingPage() {
  const { user } = useAuth()
  const { currentWorkspace } = useWorkspace()
  // Personnel › Label printing arrives with its selection as a Worker ID
  // batch (h 4602-4605). It is taken once, when the people have loaded.
  const location = useLocation()
  const shortcut = useRef<string[] | null>(((location.state as LabelShortcut | null)?.labelWorkers || null) as string[] | null)
  const [currentTab, setCurrentTab] = useState<Tab>('create')
  const [view, setView] = useState<SettingsView | null>(null)
  const [purpose, setPurpose] = useState<Purpose>(shortcut.current ? 'worker' : 'item')
  const [people, setPeople] = useState<LabelPerson[] | null>(null)
  const [peopleError, setPeopleError] = useState<string | null>(null)
  const [picked, setPicked] = useState<Set<string>>(() => new Set())
  const [jobs, setJobs] = useState<LabelJob[] | null>(null)
  const [jobsError, setJobsError] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [formatId, setFormatId] = useState('')
  const [colour, setColour] = useState('Black')
  const [assigned, setAssigned] = useState(true)
  const [idMode, setIdMode] = useState<IdMode>('Alphanumeric')
  const [fields, setFields] = useState<ContentField[]>([])
  // Until the operator changes something, the batch follows the settings: a
  // default saved in the cog shows here at once.
  const [touched, setTouched] = useState(false)
  const [cogOpen, setCogOpen] = useState(false)
  const [toast, setToast] = useState('')

  // The write in flight (h 3975-4002): the button is disabled at once and
  // says its verb only once the wait is real.
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [asking, setAsking] = useState<{ labels: number; threshold: number; draftId?: string } | null>(null)
  const { startTask } = useTasks()

  // Read again when the server refuses an action because the caller's set has
  // changed since the page loaded, so the page offers what it now allows.
  const [viewTick, setViewTick] = useState(0)
  useEffect(() => {
    if (!user) return
    let live = true
    apiJson<SettingsView>(user, '/api/label-printing/settings')
      .then((v) => {
        if (!live) return
        setView(v)
      })
      .catch((err) => live && setError(err instanceof Error ? err.message : 'Could not load the label printing settings'))
    return () => {
      live = false
    }
  }, [user, viewTick])
  const access = labelAccess(view)

  useEffect(() => {
    if (!user) return
    let live = true
    apiJson<LabelPerson[]>(user, '/api/label-printing/people')
      .then((list) => {
        if (!live) return
        const rows = Array.isArray(list) ? list : []
        setPeople(rows)
        setPeopleError(null)
        // Anyone in the shortcut's selection who is no longer active is left
        // out, and the operator is told rather than left to count.
        const asked = shortcut.current
        if (asked) {
          shortcut.current = null
          const active = new Set(rows.map((p) => p.id))
          const kept = asked.filter((id) => active.has(id))
          setPicked(new Set(kept))
          const gone = new Set(asked).size - kept.length
          if (gone) setToast(`${gone === 1 ? '1 selected person is' : `${gone} selected people are`} no longer active and ${gone === 1 ? 'was' : 'were'} left out`)
        }
      })
      .catch((err) => live && setPeopleError(err instanceof Error ? err.message : 'Could not load the people'))
    return () => {
      live = false
    }
  }, [user])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  // The settings as they are now: the start state while untouched, and the
  // capabilities always (a capability switched off takes its choice with it).
  const s = view?.settings
  useEffect(() => {
    if (!view) return
    const st = view.settings
    const p: Purpose = !st.worker_labels ? 'item' : purpose
    if (p !== purpose) setPurpose(p)
    if (!st.unassigned_items) setAssigned(true)
    // Picking job assignment switched off drops any job already chosen (h 4636).
    if (!st.job_assignment) setJobId(null)
    if (!touched) {
      setFields(startContent(st))
      setIdMode(st.start_id_mode)
      setFormatId(startFormat(view.formats, p)?.id || '')
    } else {
      if (!st.job_assignment) setFields((f) => f.filter((k) => k === 'group'))
      if (!formatsFor(view.formats, p).some((f) => f.id === formatId)) setFormatId(startFormat(view.formats, p)?.id || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  // The picking jobs, read again whenever the capability changes, so a job
  // that has since stopped being live is dropped rather than sent.
  const jobsOn = !!s?.job_assignment
  useEffect(() => {
    if (!user || !jobsOn) return
    let live = true
    setJobs(null)
    apiJson<LabelJob[]>(user, '/api/label-printing/jobs')
      .then((list) => {
        if (!live) return
        const rows = Array.isArray(list) ? list : []
        setJobs(rows)
        setJobsError(null)
        setJobId((id) => (id && rows.some((j) => j.id === id) ? id : null))
      })
      .catch((err) => live && setJobsError(err instanceof Error ? err.message : 'Could not load the picking jobs'))
    return () => {
      live = false
    }
  }, [user, jobsOn])

  const formats = view ? formatsFor(view.formats, purpose) : []
  const format = formats.find((f) => f.id === formatId)
  const isWorker = purpose === 'worker'
  const canColour = !!format?.supports_colour
  const regions = format?.supported_optional_fields || []
  const offered = CONTENT.filter((c) => (isWorker ? c.key === 'group' : c.key === 'group' || !!s?.job_assignment))
  const job = !isWorker && jobsOn ? (jobs || []).find((j) => j.id === jobId) : undefined
  // The job's name and variety print only with a job to take them from; the
  // boxes keep their state so choosing a job prints what the batch started with.
  const printFields = fields.filter((k) => regions.includes(k) && offered.some((c) => c.key === k) && (k === 'group' || !!job))
  const qty = Math.max(0, parseInt(quantity, 10) || 0)
  // Sent, and so printed, in the picker's name order, whatever order they
  // were ticked in.
  const selectedWorkers = (people || []).filter((p) => picked.has(p.id)).map((p) => p.id)
  const nWorkers = selectedWorkers.length
  const total = isWorker ? nWorkers : assigned ? qty * nWorkers : qty
  const step = (n: number) => (isWorker && n >= 6 ? n - 1 : n)

  const touch = () => setTouched(true)

  // The selection follows a change of purpose: switching moves to the new
  // purpose's default format rather than keeping the old one (§7m.3 stage 6).
  const choosePurpose = (p: Purpose) => {
    if (p === purpose || !view) return
    setPurpose(p)
    setFormatId(startFormat(view.formats, p)?.id || '')
  }

  const flipAssigned = () => {
    if (!s?.unassigned_items) {
      setToast(UNASSIGNED_OFF)
      return
    }
    touch()
    setAssigned(!assigned)
  }

  const flipField = (k: ContentField) => {
    touch()
    setFields((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]))
  }

  // Generate revalidates, commits the identities, then renders (h 4702-4704).
  // Above the threshold it asks first (h 4714-4720); the server refuses a
  // large Generate without the confirmation, and if its count differs from
  // the page's the dialog opens on the server's, holding the draft it made
  // so confirming does not make a second one. A large batch comes back still
  // Generating and is watched as a background task; a render that fails
  // leaves the batch Failed with its identities held, for Retry.
  const threshold = Number(s?.large_batch_threshold) || 2000
  const generate = async (confirmLarge: boolean, draftId?: string) => {
    if (!format || generating) return
    setGenerating(true)
    setError(null)
    let id = draftId
    // The batch and its generate are one action in the workspace the person pressed Generate in: if they
    // switch while the batch is being saved, the generate still names that batch's workspace.
    const here = inCurrentWorkspace(user)
    try {
      if (!id) {
        const batch = await apiJson<{ id: string }>(here, '/api/label-printing/batches', {
          body: {
            purpose,
            format_id: format.id,
            colour: canColour ? colour : 'Black',
            quantity: isWorker ? 1 : qty,
            worker_ids: isWorker || assigned ? selectedWorkers : [],
            assigned: isWorker ? true : assigned,
            print_fields: printFields,
            ...(job ? { picking_job_id: job.id } : {}),
            ...(isWorker ? {} : { display_id_mode: idMode }),
          },
        })
        id = batch.id
      }
      const b = await apiJson<{ id: string; status: string; label_count: number }>(here, `/api/label-printing/batches/${id}/generate`, {
        body: confirmLarge ? { confirm_large: true } : {},
      })
      setAsking(null)
      const n = b.label_count || total
      const what = isWorker ? plural(n, 'Worker ID label') : plural(n, 'item label')
      const id8 = b.id.slice(0, 8)
      if (b.status === 'Generating') startTask(renderTask(here, b.id, n, 'file'))
      else if (b.status === 'Failed')
        setError(`${what} committed as ${id8}, but the file failed to render. Nothing was lost: the identities are held, and Retry in Print history renders them again.`)
      else setToast(`${what} generated as ${id8}. Download it from Print history to issue it.`)
      setCurrentTab('history')
    } catch (err) {
      if (err instanceof ApiError && err.body?.code === 'LARGE_BATCH_CONFIRM' && id) {
        setAsking({ labels: Number(err.body.labels) || total, threshold: Number(err.body.threshold) || threshold, draftId: id })
      } else {
        setAsking(null)
        setError(err instanceof Error ? err.message : 'Unknown error')
        if (err instanceof ApiError && (err.body?.code === 'PERMISSION_CHANGED' || err.body?.code === 'NOT_PERMITTED')) setViewTick((t) => t + 1)
      }
    } finally {
      setGenerating(false)
    }
  }
  const mayGenerate = access.generate(purpose)
  // Why Generate cannot run yet (V4 lpBlocked): the button keeps its place and
  // says what is missing, and pressing it says what to do. An assigned batch
  // with nobody selected never quietly becomes an unassigned batch.
  const blocked = !format
    ? 'Choose a format first.'
    : isWorker
      ? nWorkers
        ? null
        : 'Select at least one active person.'
      : qty <= 0
        ? 'Enter a quantity above zero.'
        : assigned && !nWorkers
          ? s?.unassigned_items
            ? 'Select the workers these items belong to, or turn worker assignment off.'
            : 'Select at least one worker. This subscription cannot print unassigned items.'
          : null
  const genLabel = blocked
    ? isWorker && format
      ? 'Nobody selected yet'
      : format && assigned && !nWorkers && qty > 0
        ? 'No workers selected yet'
        : 'Nothing to generate'
    : `Generate ${plural(total, 'label')}`
  const handleGenerateBatch = () => {
    if (generating || !mayGenerate) return
    if (blocked) {
      setToast(blocked)
      return
    }
    if (total > threshold) setAsking({ labels: total, threshold })
    else generate(false)
  }

  const large = total > threshold
  const createReady = currentTab === 'create' && !!view && !!s
  const banner = error && (
    <div className={styles.errorBanner} role="alert">
      <p>{error}</p>
    </div>
  )
  const sampleId = isWorker ? '0142' : idMode === 'Numeric' ? '1042' : '04KM'

  return (
    <>
      <PageHeader
        title="Label printing"
        actions={
          <>
            <div className={styles.modeTabs} role="tablist" aria-label="Label printing tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  id={`label-printing-tab-${t.key}`}
                  aria-selected={currentTab === t.key}
                  aria-controls="label-printing-panel"
                  className={[styles.modeTab, currentTab === t.key ? styles.modeTabOn : ''].join(' ')}
                  onClick={() => setCurrentTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={headerStyles.iconBtn}
              title="Label printing settings"
              aria-label="Label printing settings"
              disabled={!view}
              onClick={() => setCogOpen(true)}
            >
              <Gear />
            </button>
          </>
        }
      />

      <PageBody className={createReady ? styles.createBody : undefined}>
        <div
          role="tabpanel"
          id="label-printing-panel"
          aria-labelledby={`label-printing-tab-${currentTab}`}
          className={createReady ? styles.formContainer : styles.panel}
        >
          {!createReady && banner}

          {currentTab === 'create' && !view && !error && <div className={styles.loading}>Loading the label printing settings…</div>}

          {createReady && view && s && (
            <>
              <div className={styles.leftPane}>
                <Stage title="1 · What are you labelling">
                  <div className={styles.purposeCards} role="radiogroup" aria-label="What are you labelling">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!isWorker}
                      className={[styles.purposeCard, !isWorker ? styles.purposeOn : ''].join(' ')}
                      onClick={() => choosePurpose('item')}
                    >
                      <span className={styles.purposeHead}>
                        <span className={[styles.dot, !isWorker ? styles.dotOn : ''].join(' ')} aria-hidden="true" />
                        <span className={styles.purposeTitle}>Item tracking labels</span>
                      </span>
                      <span className={styles.purposeDesc}>A unique label for one physical thing: a bucket, tote, crate or bin.</span>
                      <span className={styles.purposeMeta}>
                        {s.job_assignment ? 'Can carry a default worker and a picking job' : 'Can carry a default worker'}
                      </span>
                    </button>
                    {s.worker_labels && (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={isWorker}
                        className={[styles.purposeCard, isWorker ? styles.purposeOn : ''].join(' ')}
                        onClick={() => choosePurpose('worker')}
                      >
                        <span className={styles.purposeHead}>
                          <span className={[styles.dot, isWorker ? styles.dotOn : ''].join(' ')} aria-hidden="true" />
                          <span className={styles.purposeTitle}>Worker ID labels</span>
                        </span>
                        <span className={styles.purposeDesc}>A reusable label that identifies a person when it is scanned.</span>
                        <span className={styles.purposeMeta}>One per selected worker · lanyard, hard hat, timesheet</span>
                      </button>
                    )}
                  </div>
                </Stage>

                <Stage title={isWorker ? '2 · Who gets a label' : '2 · What these items belong to'}>
                  {!isWorker && (
                    <div className={styles.card}>
                      {s.unassigned_items ? (
                        <button type="button" role="checkbox" aria-checked={assigned} className={styles.assignBox} onClick={flipAssigned}>
                          <Tick on={assigned} />
                          <span className={styles.assignText}>
                            <span className={styles.assignName}>Assign these items to workers</span>
                            <span className={styles.assignNote}>Each label still carries its own unique identity. The worker becomes the item's default picker.</span>
                          </span>
                        </button>
                      ) : (
                        <button type="button" role="checkbox" aria-checked={true} aria-disabled={true} className={styles.assignBox} onClick={flipAssigned}>
                          <Tick on locked />
                          <Padlock />
                          <span className={styles.assignText}>
                            <span className={styles.assignName}>Assigned to workers: every item belongs to someone</span>
                            <span className={styles.assignNote}>
                              Each label carries its own unique identity, with the worker as its default picker. This subscription cannot print unassigned
                              items: Dataphyll enables that separately.
                            </span>
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                  {(isWorker || assigned) && <PeoplePicker people={people} failed={peopleError} selected={picked} onChange={setPicked} />}
                  {!isWorker && jobsOn && (
                    <JobPicker
                      jobs={jobs}
                      failed={jobsError}
                      value={job ? job.id : null}
                      onChange={(id) => {
                        touch()
                        setJobId(id)
                      }}
                    />
                  )}
                </Stage>

                <Stage title="3 · How many">
                  <div className={[styles.card, styles.cardRow].join(' ')}>
                    {isWorker ? (
                      <span className={styles.qtyNote}>
                        One Worker ID label per selected person. There is no copies control. A worker's label always identifies the same person, so
                        reprinting later is safe.
                      </span>
                    ) : (
                      <>
                        <input
                          type="number"
                          min="1"
                          aria-label="Quantity"
                          value={quantity}
                          onChange={(e) => {
                            touch()
                            setQuantity(e.target.value.replace(/[^0-9]/g, ''))
                          }}
                          className={styles.numberInput}
                        />
                        <span className={styles.qtyNote}>{assigned ? 'unique labels per selected worker' : 'unique item labels in this batch'}</span>
                      </>
                    )}
                    <span className={styles.qtyTotal}>{plural(total, 'label')}</span>
                  </div>
                </Stage>

                <Stage title="4 · What prints on the label">
                  <div className={[styles.card, styles.fieldCard].join(' ')}>
                    {offered.map((c) => {
                      const has = regions.includes(c.key)
                      const waiting = has && c.key !== 'group' && !job
                      return (
                        <label key={c.key} className={[styles.fieldRow, has ? '' : styles.fieldOff].join(' ')}>
                          <input
                            type="checkbox"
                            className={styles.box}
                            checked={has && fields.includes(c.key)}
                            disabled={!has}
                            onChange={() => flipField(c.key)}
                          />
                          <span className={styles.fieldText}>
                            <span className={styles.fieldTitle}>{c.title}</span>
                            <span className={styles.fieldNote}>
                              {!has
                                ? `${format?.name || 'This format'} has no region for this field, so it cannot be switched on.`
                                : waiting
                                  ? 'Prints once a picking job is chosen. Without one there is nothing to print here.'
                                  : c.note}
                            </span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </Stage>

                {!isWorker && (
                  <Stage title="5 · Display ID">
                    <div className={[styles.card, styles.cardRow].join(' ')}>
                      <div className={styles.seg} role="radiogroup" aria-label="Display ID">
                        {(['Alphanumeric', 'Numeric'] as IdMode[]).map((m) => (
                          <button
                            key={m}
                            type="button"
                            role="radio"
                            aria-checked={idMode === m}
                            className={[styles.segBtn, idMode === m ? styles.segOn : ''].join(' ')}
                            onClick={() => {
                              touch()
                              setIdMode(m)
                            }}
                          >
                            {m === 'Numeric' ? 'Numeric only' : 'Alphanumeric'}
                          </button>
                        ))}
                      </div>
                      <span className={styles.cardNote}>{ID_MODE_NOTE[idMode]}</span>
                    </div>
                  </Stage>
                )}

                <Stage title={`${step(6)} · Format`}>
                  {formats.length === 0 ? (
                    <div className={styles.card}>
                      <p className={styles.cardNote}>
                        No enabled format prints {isWorker ? 'Worker ID' : 'item tracking'} labels. An admin enables formats in Label printing settings.
                      </p>
                    </div>
                  ) : (
                    <div className={[styles.card, styles.listCard].join(' ')} role="radiogroup" aria-label="Format">
                      {formats.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          role="radio"
                          aria-checked={f.id === formatId}
                          className={[styles.formatOption, f.id === formatId ? styles.formatOn : ''].join(' ')}
                          onClick={() => {
                            touch()
                            setFormatId(f.id)
                          }}
                        >
                          <span className={[styles.dot, f.id === formatId ? styles.dotOn : ''].join(' ')} aria-hidden="true" />
                          <span className={styles.formatText}>
                            <span className={styles.formatTitle}>{f.name}</span>
                            <span className={styles.formatSub}>{formatLine(f)}</span>
                          </span>
                          <span className={[styles.formatTag, f.supports_colour ? styles.formatTagColour : ''].join(' ')}>
                            {f.supports_colour ? 'Colour' : 'Black only'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </Stage>

                {canColour && (
                  <Stage title={`${step(7)} · Display ID colour`}>
                    <div className={[styles.card, styles.colourCard].join(' ')}>
                      <div className={styles.colourOptions} role="radiogroup" aria-label="Display ID colour">
                        {COLOURS.map((c) => (
                          <button
                            key={c.name}
                            type="button"
                            role="radio"
                            aria-checked={colour === c.name}
                            className={[styles.colourButton, colour === c.name ? styles.colourOn : ''].join(' ')}
                            onClick={() => {
                              touch()
                              setColour(c.name)
                            }}
                          >
                            <span className={styles.colourSample} style={{ backgroundColor: c.bg, color: c.fg }} aria-hidden="true">
                              {sampleId}
                            </span>
                            {c.name}
                          </button>
                        ))}
                      </div>
                      <span className={styles.cardNote}>
                        One colour for the whole batch. The QR stays black on white; colour applies only to the Display ID block. Every new batch starts at
                        Black.
                      </span>
                    </div>
                  </Stage>
                )}
              </div>

              <div className={styles.rightPane}>
                <div className={styles.side}>
                  <div className={styles.generated} role="group" aria-label="Generation summary">
                    <div className={styles.generatedKicker}>Will be generated</div>
                    <div className={styles.generatedBig}>{isWorker ? plural(total, 'worker label') : plural(total, 'item label')}</div>
                    <dl className={styles.generatedList}>
                      <div>
                        <dt>Labels</dt>
                        <dd>{isWorker ? plural(total, 'Worker ID label') : plural(total, 'unique item label')}</dd>
                      </div>
                      <div>
                        <dt>Assigned to</dt>
                        <dd>{isWorker || assigned ? (nWorkers ? plural(nWorkers, 'person', 'people') : 'Nobody selected yet') : 'No worker'}</dd>
                      </div>
                      {!isWorker && jobsOn && (
                        <div>
                          <dt>Picking job</dt>
                          <dd>{job ? `${job.name} · ${job.site_name}` : 'None'}</dd>
                        </div>
                      )}
                      <div>
                        <dt>Printed text</dt>
                        <dd>{printFields.length ? printFields.map((k) => CONTENT.find((c) => c.key === k)?.title).join(', ') : 'Display ID only'}</dd>
                      </div>
                      <div>
                        <dt>QR type</dt>
                        <dd>{qrTypeLine(purpose, isWorker || assigned)}</dd>
                      </div>
                      <div>
                        <dt>Format</dt>
                        <dd>
                          {format
                            ? `${format.name} · ${format.labels_per_sheet} per ${format.medium === 'Roll' ? 'roll' : 'A4 sheet'} · template v${format.version}`
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>Display ID</dt>
                        <dd>{isWorker ? 'System UserId (already numeric)' : idModeLine(idMode)}</dd>
                      </div>
                      <div>
                        <dt>Colour</dt>
                        <dd>{canColour ? colour : 'Black. This format prints black only'}</dd>
                      </div>
                      <div>
                        <dt>Lifecycle</dt>
                        <dd>{isWorker ? 'Not applicable to worker labels' : `${s.item_lifecycle} (subscription setting)`}</dd>
                      </div>
                    </dl>
                  </div>

                  <DraftPreview
                    purpose={purpose}
                    format={format}
                    colour={colour}
                    canColour={canColour}
                    idMode={idMode}
                    named={isWorker || assigned}
                    people={(people || []).filter((p) => picked.has(p.id))}
                    job={job}
                    printFields={printFields}
                    total={total}
                  />

                  {banner}

                  <PendingButton
                    className={[styles.generateButton, blocked || !mayGenerate ? styles.generateBlocked : ''].join(' ')}
                    onClick={handleGenerateBatch}
                    busy={generating}
                    pending="Generating…"
                    disabled={!mayGenerate}
                    aria-disabled={blocked ? true : undefined}
                  >
                    {genLabel}
                  </PendingButton>
                  <p className={styles.footnote}>
                    {!mayGenerate
                      ? notAllowed(access.role, isWorker ? 'generate Worker ID labels' : 'generate item tracking labels')
                      : large
                        ? `${plural(total, 'label')} is above the ${threshold.toLocaleString('en-NZ')} label threshold: Generate asks you to confirm, commits them as one batch, and renders it in the background.`
                        : 'Generate commits the identities, then renders the file that is kept exactly as printed.'}
                  </p>
                </div>
              </div>
            </>
          )}

          {currentTab === 'history' && <PrintHistory user={user} toast={setToast} access={access} onAccessChanged={() => setViewTick((t) => t + 1)} />}
        </div>
      </PageBody>

      {asking && (
        <LargeBatchConfirm
          labels={asking.labels}
          threshold={asking.threshold}
          busy={generating}
          onConfirm={() => generate(true, asking.draftId)}
          onClose={() => setAsking(null)}
        />
      )}

      {cogOpen && view && (
        <LabelSettingsModal
          user={user}
          tenantName={currentWorkspace?.name || ''}
          view={view}
          onChange={setView}
          onClose={() => setCogOpen(false)}
          toast={setToast}
        />
      )}

      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}
